/**
 * Admin Capacity Planning
 * 
 * Dashboard de planejamento de capacidade e projeções de storage/custo.
 * 3 colunas: Real | Estimado | Impacto
 */

import { useState, useEffect } from 'preact/hooks';
import { Line, Pie, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Card, Button, Input, PageLoading } from '../components/ui';
import { T } from '../styles/tokens';
import { api } from '../api/client';

// Registrar componentes Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface CapacityMetrics {
  // Base (7 campos originais)
  active_customers: number;
  avg_agents_per_customer: number;
  avg_requests_per_agent_per_day: number;
  avg_tokens_per_request: number;
  d1_size_gb: number;
  r2_size_gb: number;
  total_requests_last_7d: number;

  // Expandido: TOP 15 Métricas (FASE 1)
  
  // Custos
  total_cost_usd: number;
  cost_per_day_usd: number;
  cost_per_request_usd: number;
  
  // Tokens
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  
  // Performance
  avg_latency_ms: number;
  error_rate_percent: number;
  uptime_percent: number;
  
  // Providers/Models
  dominant_provider: string;
  top_model: string;
  streaming_percent: number;
  
  // Storage
  total_rows_d1: number;
  rows_per_day_growth: number;
  days_until_d1_full: number;
  
  // Business
  total_customers_all_time: number;
  budget_used_monthly_usd: number;
  projected_cost_monthly_usd: number;
}

interface CapacityInputs {
  customers: number;
  avg_agents_per_customer: number;
  avg_requests_per_agent_per_day: number;
  avg_tokens_per_request: number;
  avg_bytes_per_log_entry: number;
  d1_retention_days: number;
  r2_retention_days: number;
  growth_rate_monthly_percent: number;
}

interface ImpactMetrics {
  requests_per_day: number;
  d1_rows: number;
  d1_size_gb: number;
  r2_rows: number;
  r2_size_gb: number;
  d1_cost_usd: number;
  r2_cost_usd: number;
  total_cost_usd: number;
  days_until_d1_full: number;
  alerts: Alert[];
}

interface Alert {
  level: 'info' | 'warning' | 'critical';
  message: string;
}

interface ProjectionPoint {
  label: string;
  days_offset: number;
  d1_gb: number;
  r2_gb: number;
  cost_usd: number;
  requests_per_day: number;
}

export default function AdminCapacityPlanning() {
  const [metrics, setMetrics] = useState<CapacityMetrics | null>(null);
  const [inputs, setInputs] = useState<CapacityInputs>({
    customers: 20,
    avg_agents_per_customer: 1.5,
    avg_requests_per_agent_per_day: 100,
    avg_tokens_per_request: 5200,
    avg_bytes_per_log_entry: 500,
    d1_retention_days: 7,
    r2_retention_days: 90,
    growth_rate_monthly_percent: 0,
  });
  const [impact, setImpact] = useState<ImpactMetrics | null>(null);
  const [projections, setProjections] = useState<ProjectionPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);

  // FASE 2: Dados dos gráficos adicionais
  const [providersData, setProvidersData] = useState<any[]>([]);
  const [modelsData, setModelsData] = useState<any[]>([]);
  const [costDailyData, setCostDailyData] = useState<any[]>([]);

  useEffect(() => {
    loadCurrentMetrics();
    loadChartsData(); // FASE 2: carregar dados dos gráficos
    const savedInputs = localStorage.getItem('capacity-planning-inputs');
    if (savedInputs) {
      try {
        setInputs(JSON.parse(savedInputs));
      } catch {}
    }
  }, []);

  useEffect(() => {
    if (metrics) {
      calculateImpact();
      localStorage.setItem('capacity-planning-inputs', JSON.stringify(inputs));
    }
  }, [inputs]);

  async function loadCurrentMetrics() {
    try {
      const res: any = await api.get('/admin/capacity/current-metrics');
      setMetrics(res.metrics);
      setInputs((prev) => ({
        ...prev,
        customers: res.metrics.active_customers,
        avg_agents_per_customer: res.metrics.avg_agents_per_customer,
        avg_requests_per_agent_per_day: res.metrics.avg_requests_per_agent_per_day,
        avg_tokens_per_request: res.metrics.avg_tokens_per_request,
      }));
    } catch (err) {
      console.error('Erro ao carregar métricas:', err);
    } finally {
      setLoading(false);
    }
  }

  // FASE 2: Carregar dados dos gráficos
  async function loadChartsData() {
    try {
      const [providers, models, costDaily] = await Promise.all([
        api.get('/admin/capacity/charts/providers'),
        api.get('/admin/capacity/charts/models'),
        api.get('/admin/capacity/charts/cost-daily'),
      ]);
      
      setProvidersData((providers as any).data || []);
      setModelsData((models as any).data || []);
      setCostDailyData((costDaily as any).data || []);
    } catch (err) {
      console.error('Erro ao carregar dados dos gráficos:', err);
    }
  }

  async function calculateImpact() {
    setCalculating(true);
    try {
      const res: any = await api.post('/admin/capacity/calculate', { inputs });
      setImpact(res.impact);
      setProjections(res.projections);
    } catch (err) {
      console.error('Erro ao calcular impacto:', err);
    } finally {
      setCalculating(false);
    }
  }

  async function applyPreset(name: 'current' | '7days' | '30days' | '60days' | 'aggressive') {
    try {
      const res: any = await api.get(`/admin/capacity/presets/${name}`);
      setInputs(res.preset);
    } catch (err) {
      console.error('Erro ao aplicar preset:', err);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // FASE 2: Dados dos Gráficos Adicionais
  // ─────────────────────────────────────────────────────────────

  // 1. Custo/Dia (Line Chart - últimos 30 dias)
  const costDailyChartData = {
    labels: costDailyData.map((d) => d.date),
    datasets: [
      {
        label: 'Custo/Dia (USD)',
        data: costDailyData.map((d) => d.cost),
        borderColor: T.primary,
        backgroundColor: `${T.primary}20`,
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const costDailyChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' as const, labels: { color: T.text } },
      title: { display: true, text: '💰 Custo por Dia (últimos 30 dias)', color: T.text, font: { size: 16, weight: '600' } },
    },
    scales: {
      y: { ticks: { color: T.textMuted }, grid: { color: T.border } },
      x: { ticks: { color: T.textMuted }, grid: { color: T.border } },
    },
  };

  // 2. Breakdown Providers (Pie Chart)
  const providersChartData = {
    labels: providersData.map((p) => p.provider),
    datasets: [
      {
        label: 'Requisições',
        data: providersData.map((p) => p.count),
        backgroundColor: [
          '#3B82F6', // Azul
          '#10B981', // Verde
          '#F59E0B', // Amarelo
          '#EF4444', // Vermelho
          '#8B5CF6', // Roxo
        ],
      },
    ],
  };

  const providersChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'right' as const, labels: { color: T.text } },
      title: { display: true, text: '🌐 Breakdown de Providers', color: T.text, font: { size: 16, weight: '600' } },
    },
  };

  // 3. Top Models (Bar Chart)
  const modelsChartData = {
    labels: modelsData.map((m) => m.model),
    datasets: [
      {
        label: 'Requisições',
        data: modelsData.map((m) => m.count),
        backgroundColor: T.primary,
      },
      {
        label: 'Custo (USD)',
        data: modelsData.map((m) => m.cost),
        backgroundColor: T.warning || '#F59E0B',
      },
    ],
  };

  const modelsChartOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'top' as const, labels: { color: T.text } },
      title: { display: true, text: '🤖 Top 5 Models (últimos 7 dias)', color: T.text, font: { size: 16, weight: '600' } },
    },
    scales: {
      y: { ticks: { color: T.textMuted }, grid: { color: T.border } },
      x: { ticks: { color: T.textMuted }, grid: { color: T.border } },
    },
  };

  // ─────────────────────────────────────────────────────────────
  // Gráfico de Projeção (Existente)
  // ─────────────────────────────────────────────────────────────

  const chartData = {
    labels: projections.map((p) => p.label),
    datasets: [
      {
        label: 'D1 Storage (GB)',
        data: projections.map((p) => p.d1_gb),
        borderColor: T.primary,
        backgroundColor: `${T.primary}20`,
        fill: true,
        tension: 0.4,
      },
      {
        label: 'R2 Storage (GB)',
        data: projections.map((p) => p.r2_gb),
        borderColor: T.success,
        backgroundColor: `${T.success}20`,
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Custo Total ($)',
        data: projections.map((p) => p.cost_usd),
        borderColor: T.warning || '#F59E0B',
        backgroundColor: `${T.warning || '#F59E0B'}20`,
        fill: true,
        tension: 0.4,
        yAxisID: 'y1',
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Storage (GB)',
          color: T.text,
        },
        ticks: { color: T.textMuted },
        grid: { color: T.border },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Custo (USD)',
          color: T.text,
        },
        ticks: { color: T.textMuted },
        grid: { drawOnChartArea: false },
      },
      x: {
        ticks: { color: T.textMuted },
        grid: { color: T.border },
      },
    },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { color: T.text },
      },
      title: {
        display: true,
        text: 'Projeção de Crescimento (90 dias)',
        color: T.text,
        font: { size: 16, weight: '600' },
      },
    },
  };

  if (loading) return <PageLoading />;

  return (
    <div style={{ padding: T.space6, background: T.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: T.space6 }}>
        <h1 style={{ fontSize: T.text3xl, fontWeight: T.weightBold, color: T.text, marginBottom: T.space2 }}>
          📊 Capacity Planning
        </h1>
        <p style={{ color: T.textMuted, fontSize: T.textSm }}>
          Planejamento de capacidade e projeções de storage/custo (D1 + R2)
        </p>
      </div>

      {/* Presets */}
      <div style={{ display: 'flex', gap: T.space2, marginBottom: T.space6, flexWrap: 'wrap' }}>
        <Button variant="outline" size="sm" onClick={() => applyPreset('current')}>
          📊 Atual
        </Button>
        <Button variant="outline" size="sm" onClick={() => applyPreset('7days')}>
          🚀 +7 dias (2.5x)
        </Button>
        <Button variant="outline" size="sm" onClick={() => applyPreset('30days')}>
          📈 +30 dias (10x)
        </Button>
        <Button variant="outline" size="sm" onClick={() => applyPreset('60days')}>
          🔥 +60 dias (25x)
        </Button>
        <Button variant="outline" size="sm" onClick={() => applyPreset('aggressive')}>
          💥 Agressivo (100x)
        </Button>
      </div>

      {/* 3 Colunas */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: T.space6,
        marginBottom: T.space6,
      }}>
        {/* Coluna 1: REAL */}
        <Card>
          <h2 style={{ fontSize: T.textLg, fontWeight: T.weightSemibold, color: T.text, marginBottom: T.space4 }}>
            📊 REAL (Últimos 7 dias)
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: T.space4 }}>
            {/* Base Metrics */}
            <div>
              <MetricRow icon="👥" label="Clientes Ativos" value={metrics?.active_customers || 0} unit="usuários" />
              <MetricRow icon="🤖" label="Agentes por Cliente" value={metrics?.avg_agents_per_customer || 0} unit="média" decimals={1} />
              <MetricRow icon="📊" label="Req/dia por Agente" value={metrics?.avg_requests_per_agent_per_day || 0} unit="req" />
              <MetricRow icon="🎯" label="Tokens Médios" value={metrics?.avg_tokens_per_request || 0} unit="tokens" />
              <MetricRow icon="📦" label="Total Requisições" value={metrics?.total_requests_last_7d || 0} unit="requisições" />
            </div>

            {/* 💰 Custos */}
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: T.space3 }}>
              <h3 style={{ fontSize: T.textSm, fontWeight: T.weightSemibold, color: T.text, marginBottom: T.space2 }}>
                💰 Custos
              </h3>
              <MetricRow 
                icon="💵" 
                label="Total (7d)" 
                value={metrics?.total_cost_usd || 0} 
                unit="USD" 
                decimals={2} 
                tooltip="Soma de todos os custos das requisições nos últimos 7 dias. Inclui todos os providers (Anthropic, OpenAI, Google)."
              />
              <MetricRow 
                icon="📅" 
                label="Custo/Dia" 
                value={metrics?.cost_per_day_usd || 0} 
                unit="USD/dia" 
                decimals={2} 
                tooltip="Custo médio por dia (Total 7d ÷ 7). Use para projetar gastos mensais (~30x)."
              />
              <MetricRow 
                icon="📊" 
                label="Custo/Requisição" 
                value={metrics?.cost_per_request_usd || 0} 
                unit="USD" 
                decimals={4} 
                tooltip="Custo médio por requisição. Varia conforme modelo usado (Opus > Sonnet > Haiku)."
              />
            </div>

            {/* 🔑 Tokens */}
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: T.space3 }}>
              <h3 style={{ fontSize: T.textSm, fontWeight: T.weightSemibold, color: T.text, marginBottom: T.space2 }}>
                🔑 Tokens
              </h3>
              <MetricRow icon="⬇️" label="Input" value={formatLargeNumber(metrics?.total_input_tokens || 0)} unit="tokens" />
              <MetricRow icon="⬆️" label="Output" value={formatLargeNumber(metrics?.total_output_tokens || 0)} unit="tokens" />
              <MetricRow icon="🔢" label="Total" value={formatLargeNumber(metrics?.total_tokens || 0)} unit="tokens" />
            </div>

            {/* ⚡ Performance */}
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: T.space3 }}>
              <h3 style={{ fontSize: T.textSm, fontWeight: T.weightSemibold, color: T.text, marginBottom: T.space2 }}>
                ⚡ Performance
              </h3>
              <MetricRow 
                icon="⏱️" 
                label="Latência Média" 
                value={metrics?.avg_latency_ms || 0} 
                unit="ms" 
                tooltip="Tempo médio de resposta das requisições (desde envio até receber resposta completa). Inclui tempo de rede + processamento do LLM."
              />
              <MetricRow 
                icon="✅" 
                label="Uptime" 
                value={metrics?.uptime_percent || 0} 
                unit="%" 
                decimals={2} 
                tooltip="Percentual de requisições bem-sucedidas (100% - taxa de erro). Meta: >99%."
              />
              <MetricRow 
                icon="🔴" 
                label="Taxa de Erro" 
                value={metrics?.error_rate_percent || 0} 
                unit="%" 
                decimals={2} 
                tooltip="Percentual de requisições que falharam (status=error). Inclui timeouts, quota exceeded, API errors, etc."
              />
            </div>

            {/* 🌐 Providers/Models */}
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: T.space3 }}>
              <h3 style={{ fontSize: T.textSm, fontWeight: T.weightSemibold, color: T.text, marginBottom: T.space2 }}>
                🌐 Providers & Models
              </h3>
              <MetricRow icon="🏢" label="Provider Dominante" value={metrics?.dominant_provider || 'unknown'} unit="" />
              <MetricRow icon="🤖" label="Top Model" value={metrics?.top_model || 'unknown'} unit="" />
              <MetricRow icon="📡" label="Streaming" value={metrics?.streaming_percent || 0} unit="%" decimals={1} />
            </div>

            {/* 📦 Storage */}
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: T.space3 }}>
              <h3 style={{ fontSize: T.textSm, fontWeight: T.weightSemibold, color: T.text, marginBottom: T.space2 }}>
                📦 Storage
              </h3>
              <MetricRow 
                icon="🗄️" 
                label="D1 Size" 
                value={metrics?.d1_size_gb || 0} 
                unit="GB" 
                decimals={3} 
                tooltip="Tamanho estimado do banco D1 (router_logs). Calculado: total_rows × 500 bytes/row. Limite: 30GB."
              />
              <MetricRow 
                icon="📊" 
                label="Total Rows D1" 
                value={formatLargeNumber(metrics?.total_rows_d1 || 0)} 
                unit="rows" 
                tooltip="Número total de linhas na tabela router_logs (desde o início)."
              />
              <MetricRow 
                icon="📈" 
                label="Crescimento" 
                value={metrics?.rows_per_day_growth || 0} 
                unit="rows/dia" 
                tooltip="Taxa de crescimento diário (total_rows ÷ 7). Use para estimar quando D1 ficará cheio."
              />
              <MetricRow 
                icon="⏳" 
                label="Dias até Cheio" 
                value={metrics?.days_until_d1_full || 0} 
                unit="dias" 
                tooltip="Dias restantes até D1 atingir limite de 30GB (com taxa de crescimento atual). Se >1000 = muito tempo ainda."
              />
            </div>

            {/* 💼 Business */}
            <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: T.space3 }}>
              <h3 style={{ fontSize: T.textSm, fontWeight: T.weightSemibold, color: T.text, marginBottom: T.space2 }}>
                💼 Business
              </h3>
              <MetricRow 
                icon="👥" 
                label="Total Clientes" 
                value={metrics?.total_customers_all_time || 0} 
                unit="all-time" 
                tooltip="Número total de clientes cadastrados na tabela customers (desde sempre)."
              />
              <MetricRow 
                icon="💳" 
                label="Budget Usado" 
                value={metrics?.budget_used_monthly_usd || 0} 
                unit="USD/mês" 
                decimals={2} 
                tooltip="Soma de budget_used_monthly de todos os tokens Elon Router. Representa gastos acumulados nos tokens dos clientes."
              />
              <MetricRow 
                icon="📊" 
                label="Projeção Mensal" 
                value={metrics?.projected_cost_monthly_usd || 0} 
                unit="USD/mês" 
                decimals={2} 
                tooltip="Projeção de custo para 30 dias (Custo/Dia × 30). Baseado nos últimos 7 dias."
              />
            </div>
          </div>
        </Card>

        {/* Coluna 2: ESTIMADO */}
        <Card>
          <h2 style={{ fontSize: T.textLg, fontWeight: T.weightSemibold, color: T.text, marginBottom: T.space4 }}>
            ✏️ ESTIMADO (Editável)
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: T.space3 }}>
            <InputRow
              icon="👥"
              label="Clientes"
              value={inputs.customers}
              onChange={(v) => setInputs({ ...inputs, customers: v })}
            />
            <InputRow
              icon="🤖"
              label="Agentes por Cliente"
              value={inputs.avg_agents_per_customer}
              onChange={(v) => setInputs({ ...inputs, avg_agents_per_customer: v })}
              step={0.1}
            />
            <InputRow
              icon="📊"
              label="Req/dia por Agente"
              value={inputs.avg_requests_per_agent_per_day}
              onChange={(v) => setInputs({ ...inputs, avg_requests_per_agent_per_day: v })}
            />
            <InputRow
              icon="🎯"
              label="Tokens Médios"
              value={inputs.avg_tokens_per_request}
              onChange={(v) => setInputs({ ...inputs, avg_tokens_per_request: v })}
            />
            <InputRow
              icon="📏"
              label="Bytes por Log"
              value={inputs.avg_bytes_per_log_entry}
              onChange={(v) => setInputs({ ...inputs, avg_bytes_per_log_entry: v })}
            />
            <InputRow
              icon="⏱️"
              label="Retenção D1 (dias)"
              value={inputs.d1_retention_days}
              onChange={(v) => setInputs({ ...inputs, d1_retention_days: v })}
            />
            <InputRow
              icon="⏱️"
              label="Retenção R2 (dias)"
              value={inputs.r2_retention_days}
              onChange={(v) => setInputs({ ...inputs, r2_retention_days: v })}
            />
            <InputRow
              icon="📈"
              label="Crescimento Mensal (%)"
              value={inputs.growth_rate_monthly_percent}
              onChange={(v) => setInputs({ ...inputs, growth_rate_monthly_percent: v })}
            />
          </div>
        </Card>

        {/* Coluna 3: IMPACTO */}
        <Card>
          <div style={{ display: 'flex', alignItems: 'center', gap: T.space2, marginBottom: T.space4 }}>
            <h2 style={{ fontSize: T.textLg, fontWeight: T.weightSemibold, color: T.text }}>
              🎯 IMPACTO (Auto-calculado)
            </h2>
            {calculating && (
              <span style={{ fontSize: T.textXs, color: T.primary, fontStyle: 'italic' }}>
                Calculando...
              </span>
            )}
          </div>

          {impact && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: T.space3 }}>
              <ImpactRow icon="📊" label="Req/dia" value={impact.requests_per_day} />
              <ImpactRow
                icon="🗄️"
                label="D1 Storage"
                value={impact.d1_size_gb}
                unit="GB"
                decimals={3}
                status={impact.d1_size_gb > 25 ? 'critical' : impact.d1_size_gb > 20 ? 'warning' : 'ok'}
              />
              <ImpactRow icon="📦" label="R2 Storage" value={impact.r2_size_gb} unit="GB" decimals={3} />
              <ImpactRow icon="💰" label="Custo D1" value={impact.d1_cost_usd} unit="$/mês" decimals={2} />
              <ImpactRow icon="💰" label="Custo R2" value={impact.r2_cost_usd} unit="$/mês" decimals={2} />
              <ImpactRow
                icon="💸"
                label="Custo Total"
                value={impact.total_cost_usd}
                unit="$/mês"
                decimals={2}
                status={impact.total_cost_usd > 200 ? 'warning' : 'ok'}
              />
              <ImpactRow
                icon="⏰"
                label="D1 Cheio Em"
                value={impact.days_until_d1_full > 9000 ? Infinity : impact.days_until_d1_full}
                unit="dias"
                status={
                  impact.days_until_d1_full < 14 && impact.days_until_d1_full > 0
                    ? 'critical'
                    : impact.days_until_d1_full < 30 && impact.days_until_d1_full > 0
                    ? 'warning'
                    : 'ok'
                }
              />

              {/* Alertas */}
              {impact.alerts.length > 0 && (
                <div style={{ marginTop: T.space2, display: 'flex', flexDirection: 'column', gap: T.space2 }}>
                  {impact.alerts.map((alert, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: T.space3,
                        borderRadius: T.radius,
                        fontSize: T.textSm,
                        background:
                          alert.level === 'critical'
                            ? `${T.danger}15`
                            : alert.level === 'warning'
                            ? `${T.warning || '#F59E0B'}15`
                            : `${T.primary}15`,
                        color:
                          alert.level === 'critical'
                            ? T.danger
                            : alert.level === 'warning'
                            ? T.warning || '#F59E0B'
                            : T.primary,
                        border: `1px solid ${
                          alert.level === 'critical'
                            ? T.danger
                            : alert.level === 'warning'
                            ? T.warning || '#F59E0B'
                            : T.primary
                        }`,
                      }}
                    >
                      {alert.message}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* Gráfico de Projeção */}
      {projections.length > 0 && (
        <Card>
          <div style={{ padding: T.space4 }}>
            <Line data={chartData} options={chartOptions} />
          </div>
        </Card>
      )}

      {/* FASE 2: Gráficos Adicionais */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: T.space6, marginTop: T.space6 }}>
        {/* 1. Custo/Dia (Line Chart) */}
        {costDailyData.length > 0 && (
          <Card>
            <div style={{ padding: T.space4 }}>
              <Line data={costDailyChartData} options={costDailyChartOptions} />
            </div>
          </Card>
        )}

        {/* 2. Breakdown Providers (Pie Chart) */}
        {providersData.length > 0 && (
          <Card>
            <div style={{ padding: T.space4 }}>
              <Pie data={providersChartData} options={providersChartOptions} />
            </div>
          </Card>
        )}

        {/* 3. Top Models (Bar Chart) */}
        {modelsData.length > 0 && (
          <Card>
            <div style={{ padding: T.space4 }}>
              <Bar data={modelsChartData} options={modelsChartOptions} />
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

function formatLargeNumber(num: number): string {
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(1)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}

// ─────────────────────────────────────────────────────────────
// Componentes Auxiliares
// ─────────────────────────────────────────────────────────────

function MetricRow({ icon, label, value, unit, decimals = 0, tooltip }: any) {
  const [showTooltip, setShowTooltip] = useState(false);
  const displayValue = typeof value === 'number' 
    ? value.toFixed(decimals)
    : value;

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: `${T.space2}px 0`,
      borderBottom: `1px solid ${T.border}`,
      position: 'relative',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: T.space2 }}>
        <span style={{ fontSize: T.textLg }}>{icon}</span>
        <span style={{ fontSize: T.textSm, color: T.textMuted }}>{label}</span>
        {tooltip && (
          <span
            style={{ fontSize: T.textXs, color: T.primary, cursor: 'help', position: 'relative' }}
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            ℹ️
            {showTooltip && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '8px',
                padding: T.space3,
                background: T.bgCard,
                border: `1px solid ${T.border}`,
                borderRadius: T.radius,
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                color: T.text,
                fontSize: T.textXs,
                maxWidth: '300px',
                whiteSpace: 'normal',
                zIndex: 1000,
                textAlign: 'left',
              }}>
                {tooltip}
                <div style={{
                  position: 'absolute',
                  bottom: '-6px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '6px solid transparent',
                  borderRight: '6px solid transparent',
                  borderTop: `6px solid ${T.border}`,
                }}></div>
              </div>
            )}
          </span>
        )}
      </div>
      <div style={{ textAlign: 'right' }}>
        <span style={{ fontSize: T.textLg, fontWeight: T.weightSemibold, color: T.text }}>
          {displayValue}
        </span>
        {unit && <span style={{ fontSize: T.textXs, color: T.textMuted, marginLeft: 4 }}>{unit}</span>}
      </div>
    </div>
  );
}

function InputRow({ icon, label, value, onChange, step = 1 }: any) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: T.space2, marginBottom: 4 }}>
        <span style={{ fontSize: T.textBase }}>{icon}</span>
        <label style={{ fontSize: T.textSm, color: T.textMuted }}>{label}</label>
      </div>
      <input
        type="number"
        value={value}
        onChange={(e: any) => onChange(parseFloat(e.target.value) || 0)}
        step={step}
        style={{
          width: '100%',
          padding: `${T.space2}px ${T.space3}px`,
          border: `1px solid ${T.border}`,
          borderRadius: T.radius,
          background: T.bgCard,
          color: T.text,
          fontSize: T.textSm,
        }}
      />
    </div>
  );
}

function ImpactRow({ icon, label, value, unit, decimals = 0, status = 'ok' }: any) {
  const isInfinity = !isFinite(value);

  const statusColors = {
    ok: T.text,
    warning: T.warning || '#F59E0B',
    critical: T.danger,
  };

  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: `${T.space2}px 0`,
      borderBottom: `1px solid ${T.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: T.space2 }}>
        <span style={{ fontSize: T.textLg }}>{icon}</span>
        <span style={{ fontSize: T.textSm, color: T.textMuted }}>{label}</span>
      </div>
      <div style={{ textAlign: 'right' }}>
        <span style={{
          fontSize: T.textLg,
          fontWeight: T.weightSemibold,
          color: statusColors[status as keyof typeof statusColors],
        }}>
          {isInfinity ? '∞' : value.toFixed(decimals)}
        </span>
        {unit && <span style={{ fontSize: T.textXs, color: T.textMuted, marginLeft: 4 }}>{unit}</span>}
      </div>
    </div>
  );
}
